import {
  SITValidateRequest,
  SITValidateResponse,
  WidgetIdentitySecretInterface,
  WidgetLinkVisitorRequest,
  WidgetLinkVisitorResponse
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

/**
 * Helpers for the WidgetIdentity surface — SIT validation (called by the
 * widget runtime) + Identity Secret view/rotate (called by the studio).
 *
 * Per apps/server/src/API/WidgetIdentity/WidgetIdentity.Controller.ts.
 */
export class WidgetIdentityAPI {
  /** Public — widget runtime calls this after `Swivl.identify({ token })`. */
  static async validateSit(body: SITValidateRequest) {
    return APIService.Server.post<SITValidateResponse>(`/widget-identity/sit/validate`, body);
  }

  /** Link the visitor row to a chat-proven user (ENG-3190 QA rejection, Bug 2). */
  static async linkVisitor(body: WidgetLinkVisitorRequest) {
    return APIService.Server.post<WidgetLinkVisitorResponse>(`/widget-identity/link-visitor`, body);
  }

  /** Admin — fetches (lazily mints) the org's active HMAC secret. */
  static async getSecret(orgId: number) {
    return APIService.Server.get<WidgetIdentitySecretInterface>(`/widget-identity/secret/org/${orgId}`);
  }

  /** Admin — rotates the active secret (revokes the current + mints a new row). */
  static async rotateSecret(orgId: number) {
    return APIService.Server.post<WidgetIdentitySecretInterface>(`/widget-identity/secret/org/${orgId}/rotate`);
  }

  /**
   * Swivl-admin — mint a SIT for the studio-hosted widget harness's
   * "act as customer" flow (ENG-2991). Same endpoint the local harness
   * hits with the internal token; member JWTs are allowed via stacked
   * security (`SWIVL_ADMIN_ONLY`).
   */
  static async mintSit(publicId: string, userId: number) {
    return APIService.Server.post<MintSitResult>(`/widget-identity/mint-sit`, { publicId, userId });
  }

  /**
   * Swivl-admin — chat credential for an existing user, so the harness's
   * "act as customer" can BE that person with a fresh chat rather than
   * merging an anonymous visitor into them (ENG-3047). No merge, no user
   * creation, and no tenant requirement — leads work too.
   */
  static async mintChatToken(publicId: string, userId: number) {
    return APIService.Server.post<MintChatTokenResult>(`/widget-identity/mint-chat-token`, { publicId, userId });
  }
}

/** Mirrors the server's MintSITResult (apps/server WidgetIdentity.Service). */
export type MintSitResult = { ok: true; token: string } | { ok: false; reason: "unknown_widget" | "no_tenant" | "no_external_tenant_id" };

/** Mirrors the server's MintChatTokenResult (apps/server WidgetIdentity.Service). */
export type MintChatTokenResult =
  | { ok: true; token: string; userId: number; name: string | null }
  | { ok: false; reason: "unknown_widget" | "unknown_user" | "wrong_org" | "no_token" };
